
import { Link } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { CalendarDays, Clock, ArrowLeft, Share2, ChevronRight, Bookmark, ThumbsUp, ExternalLink, BookOpen, AlertCircle } from "lucide-react";
import { Helmet } from "react-helmet";
import { useEffect, useRef, useState } from "react";
import ImageOverlay from "./common/ImageOverlay";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import OptimizedImage from "./ui/optimized-image";

interface ArticleTemplateProps {
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
  imageUrl?: string;
  content: React.ReactNode;
  relatedArticles?: Array<{
    id: string;
    title: string;
    slug: string;
    description: string;
  }>;
  onImageChange?: (newUrl: string) => void;
  showEditButton?: boolean;
}

const ArticleTemplate = ({
  title,
  description,
  date,
  readTime,
  category,
  imageUrl,
  content,
  relatedArticles = [],
  onImageChange,
  showEditButton = false
}: ArticleTemplateProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const tocRef = useRef<HTMLDivElement>(null);
  const [tocVisible, setTocVisible] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl || '/placeholder.svg');
  const isMobile = useIsMobile();

  useEffect(() => {
    setCurrentImageUrl(imageUrl || '/placeholder.svg');
  }, [imageUrl]);

  useEffect(() => {
    if (contentRef.current && tocRef.current) {
      const headings = contentRef.current.querySelectorAll('h2, h3');

      if (headings.length > 0) {
        setTocVisible(true);
        const tocList = document.createElement('ol');
        tocList.className = 'space-y-2 text-sm';

        headings.forEach((heading, index) => {
          const id = `heading-${index}`;
          heading.id = id;

          const listItem = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${id}`;
          link.className = `text-blue-600 hover:underline ${heading.tagName === 'H3' ? 'ml-4 text-sm' : ''}`;
          link.textContent = heading.textContent || '';
          link.setAttribute('aria-label', `Siirry kohtaan ${heading.textContent}`);

          listItem.appendChild(link);
          tocList.appendChild(listItem);
        });

        while (tocRef.current.firstChild) {
          tocRef.current.removeChild(tocRef.current.firstChild);
        }
        tocRef.current.appendChild(tocList);
      } else {
        setTocVisible(false);
      }
    }
  }, [content]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: description,
        url: window.location.href,
      }).catch(err => {
        console.warn("Error sharing", err);
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        const alert = document.createElement('div');
        alert.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded shadow-lg z-50';
        alert.setAttribute('role', 'alert');
        alert.textContent = 'Linkki kopioitu leikepöydälle!';
        document.body.appendChild(alert);

        setTimeout(() => {
          alert.remove();
        }, 3000);
      })
      .catch(err => console.error('Clipboard copy failed:', err));
  };

  const handleImageChange = (newImageUrl: string) => {
    console.log("Image changed in ArticleTemplate:", newImageUrl);
    setCurrentImageUrl(newImageUrl);

    if (onImageChange) {
      onImageChange(newImageUrl);
      toast.success("Artikkelin kuva päivitetty onnistuneesti!");
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>{title} - Lainasampo.fi</title>
        <meta name="description" content={description} />
        
        {/* Open Graph metadata - always use social card */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${title} - Lainasampo.fi`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://lainasampo.fi/lainasampo-social-card.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content="Lainasampo.fi - Kilpailuta lainasi ja säästä rahaa" />
        <meta property="og:site_name" content="Lainasampo.fi" />
        <meta property="og:locale" content="fi_FI" />
        
        {/* Twitter Card metadata */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} - Lainasampo.fi`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://lainasampo.fi/lainasampo-social-card.jpg" />
        <meta name="twitter:image:alt" content="Lainasampo.fi - Kilpailuta lainasi ja säästä rahaa" />
        
        {/* Article specific metadata */}
        <meta property="article:published_time" content={date} />
        <meta property="article:section" content={category} />
        <meta property="article:author" content="Lainasampo.fi" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={`https://lainasampo.fi${window.location.pathname}`} />
      </Helmet>

      <section className="bg-gradient-to-r from-blue-50 to-white py-12 border-b border-blue-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Link 
                to="/artikkelit" 
                className="inline-flex items-center text-blue-700 hover:text-blue-800 transition-colors font-medium"
                aria-label="Takaisin artikkeleihin"
              >
                <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
                Takaisin artikkeleihin
              </Link>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="article-header"
            >
              {currentImageUrl && (
                <div className="mb-8 overflow-hidden rounded-xl shadow-xl relative bg-gray-50">
                  <div className="w-full aspect-[2/1] flex items-center justify-center">
                    {showEditButton ? (
                      <ImageOverlay 
                        imageUrl={currentImageUrl}
                        alt={title}
                        onImageChange={handleImageChange}
                        aspectRatio="aspect-[2/1]"
                        className="w-full h-full"
                        imageClassName="w-full h-full object-cover object-center" 
                        showEditButton={true}
                      />
                    ) : (
                      <OptimizedImage 
                        src={currentImageUrl}
                        alt={title}
                        className="w-full h-full object-cover"
                        priority
                      />
                    )}
                  </div>
                </div>
              )}

              <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                {category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 leading-tight">{title}</h1>
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">{description}</p>
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <div className="flex items-center">
                  <CalendarDays className="h-4 w-4 mr-1" aria-hidden="true" />
                  <span className="text-sm">
                    {new Date(date).toLocaleDateString('fi-FI', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" aria-hidden="true" />
                  <span className="text-sm">Lukuaika: {readTime}</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-1" aria-hidden="true" />
                  <span className="text-sm">Vaikeustaso: Keskitaso</span>
                </div>

                <div className={`flex gap-2 ${isMobile ? 'mt-2 w-full justify-end' : 'ml-auto'}`}>
                  <button 
                    className="flex items-center text-blue-700 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded"
                    title="Tallenna artikkeli"
                    aria-label="Tallenna artikkeli"
                  >
                    <Bookmark className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button 
                    className="flex items-center text-blue-700 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded"
                    title="Tykkää artikkelista"
                    aria-label="Tykkää artikkelista"
                  >
                    <ThumbsUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button 
                    className="flex items-center text-blue-700 hover:text-blue-800 transition-colors p-1 hover:bg-blue-50 rounded"
                    title="Jaa artikkeli"
                    aria-label="Jaa artikkeli"
                    onClick={handleShare}
                  >
                    <Share2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {tocVisible && (
        <div className="container mx-auto px-4 py-6 hidden lg:block">
          <div className="max-w-4xl mx-auto flex gap-16">
            <div className="w-64 shrink-0">
              <div className="sticky top-6">
                <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-3">Sisällysluettelo</h3>
                  <div ref={tocRef} role="navigation" aria-label="Artikkelin sisällysluettelo">
                    <div className="animate-pulse space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="prose prose-blue lg:prose-lg max-w-full article-content"
                ref={contentRef}
              >
                {content}
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {!tocVisible && (
        <div className="container mx-auto px-4 py-6 lg:block">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="prose prose-blue lg:prose-lg max-w-full article-content"
              ref={contentRef}
            >
              {content}
            </motion.div>
          </div>
        </div>
      )}

      <section className="py-8 lg:hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="prose prose-blue max-w-full article-content"
              ref={contentRef}
            >
              {content}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Separator className="mb-10" />

            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 rounded-xl my-10 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-4">Haluatko kilpailuttaa lainasi?</h2>
              <p className="text-blue-100 mb-6">
                Lainasampo.fi:n avulla voit helposti kilpailuttaa lainasi ja löytää edullisimman vaihtoehdon. 
                Palvelumme on täysin maksuton ja voit saada tarjouksia jopa minuuteissa.
              </p>
              <Button asChild size="lg" className="bg-white text-blue-700 hover:bg-blue-50">
                <Link 
                  to="/kilpailuta" 
                  className="inline-flex items-center"
                  aria-label="Kilpailuta lainasi nyt"
                >
                  Kilpailuta lainasi nyt
                  <ExternalLink className="ml-2 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>

            {relatedArticles.length > 0 && (
              <div className="my-10">
                <h2 className="text-2xl font-bold mb-6 text-blue-700">Aiheeseen liittyvät artikkelit</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {relatedArticles.map((article) => (
                    <Card key={article.id} className="hover:shadow-md transition-shadow border-t-4 border-blue-500">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">
                          <Link 
                            to={`/artikkelit/${article.slug}`} 
                            className="text-blue-700 hover:text-blue-800 transition-colors"
                          >
                            {article.title}
                          </Link>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-700 text-sm mb-4 line-clamp-2">{article.description}</p>
                        <Link 
                          to={`/artikkelit/${article.slug}`} 
                          className="text-blue-600 font-medium hover:text-blue-800 transition-colors inline-flex items-center text-sm group"
                          aria-label={`Lue artikkeli: ${article.title}`}
                        >
                          Lue artikkeli
                          <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-blue-50 p-6 rounded-lg my-10 border border-blue-100">
              <h3 className="text-xl font-bold mb-3 text-blue-700">Jäikö kysyttävää?</h3>
              <p className="text-gray-700 mb-4">
                Jos sinulla on kysyttävää artikkelista tai tarvitset apua laina-asioissa, 
                ota yhteyttä asiantuntijoihimme. Autamme mielellämme!
              </p>
              <Link 
                to="/yhteydenotto" 
                className="text-blue-600 font-medium hover:underline inline-flex items-center"
                aria-label="Ota yhteyttä"
              >
                Ota yhteyttä
                <ChevronRight className="h-4 w-4 ml-1" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>
        {`
        .article-content h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e40af;
          margin-top: 2rem;
          margin-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 0.5rem;
        }

        .article-content h3 {
          font-size: 1.35rem;
          font-weight: 600;
          color: #2563eb;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .article-content p {
          margin-bottom: 1.25rem;
          line-height: 1.7;
        }

        .article-content ul, .article-content ol {
          margin-bottom: 1.5rem;
          padding-left: 1.25rem;
        }

        .article-content li {
          margin-bottom: 0.5rem;
          padding-left: 0.5rem;
        }

        .article-content ul li {
          list-style-type: disc;
        }

        .article-content ol li {
          list-style-type: decimal;
        }

        .article-content strong {
          font-weight: 600;
          color: #334155;
        }

        .article-content a {
          color: #2563eb;
          text-decoration: underline;
          text-underline-offset: 2px;
        }

        .article-content a:hover {
          color: #1d4ed8;
        }
        
        @media (max-width: 768px) {
          .article-content h2 {
            font-size: 1.5rem;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
          }
          
          .article-content h3 {
            font-size: 1.25rem;
            margin-top: 1.25rem;
            margin-bottom: 0.5rem;
          }
          
          .article-content p {
            font-size: 1rem;
            margin-bottom: 1rem;
          }
          
          .article-content ul, .article-content ol {
            margin-bottom: 1rem;
            padding-left: 1rem;
          }
          
          .flex.flex-wrap.items-center.gap-4 {
            gap: 0.5rem;
            flex-direction: column;
            align-items: flex-start;
          }
          
          .flex.flex-wrap.items-center.gap-4 > div {
            margin-bottom: 0.25rem;
          }
        }
        `}
      </style>
    </div>
  );
};

export default ArticleTemplate;
